import React, { useEffect, useState } from "react";
import Sidebar from "../Sidebar/Sidebar";
import "./Member.css";
import Navbar from "./Navbar";
import Header from "./header";
import MyTable from "./Table";
import moment from "moment";
import ApiConfig from "../../common/config/ApiConfig";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLanguage } from "../../common/i18n/LanguageContext";

const LIMIT = 20;

const ENTITY_NAMES = [
  "AdminLoginLog",
  "SubPermission",
  "AdminLogin",
  "Member",
  "Transaction",
  "Payrate",
  "MaxBet",
  "MinBet",
  "DownlineCommission",
  "FightLimit",
  "FightPercentLimit",
];

const ACTIONS = ["CREATE", "UPDATE", "DELETE", "SOFT_DELETE", "RESTORE"];

const ACTION_COLORS = {
  CREATE: "bg-green-100 text-green-700",
  UPDATE: "bg-blue-100 text-blue-700",
  DELETE: "bg-red-100 text-red-700",
  SOFT_DELETE: "bg-orange-100 text-orange-700",
  RESTORE: "bg-brand-gold-100 text-brand-gold-700",
};

function ActivityLog() {
  const { t } = useLanguage();
  const pos = localStorage.getItem("position");
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [entityName, setEntityName] = useState("");
  const [action, setAction] = useState("");
  const [userId, setUserId] = useState("");
  const [applied, setApplied] = useState({
    entityName: "",
    action: "",
    userId: "",
  });

  useEffect(() => {
    const params = new URLSearchParams({ page, limit: LIMIT });
    if (applied.entityName) params.set("entityName", applied.entityName);
    if (applied.action) params.set("action", applied.action);
    if (applied.userId) params.set("userId", applied.userId);

    ApiConfig.get(`/get_activity_log?${params.toString()}`).then((res) => {
      if (!res.resCode || res.resCode !== 200) {
        toast.error(res?.message || t("common_load_error"), {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }
      const result = res.data;
      setLogs(Array.isArray(result?.data) ? result.data : []);
      if (result?.total != null) setTotal(result.total);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, applied]);

  const handleSearch = () => {
    setPage(1);
    setApplied({ entityName, action, userId });
  };

  const totalPages = total > 0 ? Math.ceil(total / LIMIT) : 0;

  const columns = [
    {
      Header: "#",
      accessor: "id",
      align: "center",
      Cell: ({ value }) => <span className="text-xs">{value}</span>,
      widthClass: "w-[4%]",
    },
    {
      Header: t("activitylog_col_entity"),
      accessor: "entityName",
      align: "center",
      Cell: ({ value }) => <span className="font-bold text-xs">{value}</span>,
      widthClass: "w-[12%]",
    },
    {
      Header: t("activitylog_col_entity_id"),
      accessor: "entityId",
      align: "center",
      Cell: ({ value }) => <span className="text-xs">{value ?? "-"}</span>,
      widthClass: "w-[6%]",
    },
    {
      Header: t("activitylog_col_action"),
      accessor: "action",
      align: "center",
      Cell: ({ value }) => (
        <span
          className={`text-xs font-bold px-2 py-0.5 rounded-full ${ACTION_COLORS[value] ?? "bg-gray-100 text-gray-700"}`}
        >
          {value}
        </span>
      ),
      widthClass: "w-[8%]",
    },
    {
      Header: t("activitylog_col_changed_fields"),
      accessor: "changedFields",
      align: "left",
      Cell: ({ value }) => (
        <span className="text-xs text-gray-600">
          {Array.isArray(value) ? value.join(", ") : "-"}
        </span>
      ),
      widthClass: "w-[12%]",
    },
    {
      Header: t("activitylog_col_old_values"),
      accessor: "oldValues",
      align: "left",
      Cell: ({ value }) => (
        <span className="text-xs text-red-600 break-all">
          {value ? JSON.stringify(value) : "-"}
        </span>
      ),
      widthClass: "w-[18%]",
    },
    {
      Header: t("activitylog_col_new_values"),
      accessor: "newValues",
      align: "left",
      Cell: ({ value }) => (
        <span className="text-xs text-green-700 break-all">
          {value ? JSON.stringify(value) : "-"}
        </span>
      ),
      widthClass: "w-[18%]",
    },
    {
      Header: t("activitylog_col_user_id"),
      accessor: "userId",
      align: "center",
      Cell: ({ value }) => <span className="text-xs">{value ?? "-"}</span>,
      widthClass: "w-[5%]",
    },
    {
      Header: t("login_username_label"),
      accessor: "username",
      align: "center",
      Cell: ({ value }) => <span className="text-xs">{value ?? "-"}</span>,
      widthClass: "w-[9%]",
    },
    {
      Header: t("membertable_tx_col_date"),
      accessor: "createdAt",
      align: "right",
      Cell: ({ value }) => (
        <span className="text-xs">
          {moment(value).format("DD/MM/YY HH:mm:ss")}
        </span>
      ),
      widthClass: "w-[8%]",
    },
  ];

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <div className="shrink-0">
        <Navbar />
      </div>
      <div className="flex flex-1 overflow-hidden items-start">
        <div className="shrink-0 overflow-y-auto h-full">
          <Sidebar pos={pos} />
        </div>
        <div className="flex flex-col flex-1 max-h-full overflow-hidden">
          <div className="shrink-0">
            <Header title={t("menu_activity_logs")} />
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-2">
            <div className="flex flex-wrap gap-2 bg-white p-2 rounded-lg shadow-sm items-center">
              <select
                value={entityName}
                onChange={(e) => setEntityName(e.target.value)}
                className="text-sm border border-slate-300 rounded px-2 py-1"
              >
                <option value="">{t("common_all")} (Entity)</option>
                {ENTITY_NAMES.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <select
                value={action}
                onChange={(e) => setAction(e.target.value)}
                className="text-sm border border-slate-300 rounded px-2 py-1"
              >
                <option value="">{t("common_all")} (Action)</option>
                {ACTIONS.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder="User ID"
                className="text-sm border border-slate-300 rounded px-2 py-1 w-28"
              />
              <button
                onClick={handleSearch}
                className="text-sm font-bold px-4 py-1 rounded bg-brand-blue-600 text-white hover:bg-brand-blue-700 transition-colors"
              >
                {t("common_search")}
              </button>
            </div>

            <MyTable columns={columns} data={logs} />

            {(totalPages > 1 || logs.length > 0) && (
              <div className="flex items-center justify-between mt-3 px-2">
                <span className="text-sm font-semibold text-brand-blue-700 bg-brand-blue-50 px-3 py-1 rounded-full border border-brand-blue-200">
                  {t("commission_page_label")} {page}
                  {totalPages > 0 ? ` / ${totalPages}` : ""}
                  {total > 0 && ` · ${total} ${t("common_items_unit")}`}
                </span>
                <div className="flex space-x-1">
                  <button
                    onClick={() => setPage(1)}
                    disabled={page === 1}
                    className="px-2 py-1 text-xs font-bold rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    «
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 text-xs font-medium rounded bg-brand-blue-100 text-brand-blue-700 hover:bg-brand-blue-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    ‹ {t("common_page_previous")}
                  </button>
                  <span className="px-3 py-1 text-xs font-bold rounded bg-brand-blue-600 text-white select-none">
                    {page}
                  </span>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={totalPages > 0 && page >= totalPages}
                    className="px-3 py-1 text-xs font-medium rounded bg-brand-blue-100 text-brand-blue-700 hover:bg-brand-blue-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    {t("common_next")} ›
                  </button>
                  {totalPages > 0 && (
                    <button
                      onClick={() => setPage(totalPages)}
                      disabled={page >= totalPages}
                      className="px-2 py-1 text-xs font-bold rounded bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      »
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ActivityLog;
